import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { ClientVisitStatus } from '../../enums/client-visit-status.enum';
import { BaseEntity } from './base.entity';
import { ClientNoteEntity } from './client-note.entity';
import { ClientEntity } from './client.entity';

/**
 * Sales visit record — when a salesperson visits a client. Despite the
 * `m_sm_` prefix (legacy SM1 carryover) this is an actively-used CRM
 * entity in both the new app (`clients.service.ts:createVisit`,
 * scorecard reports, goals reports, AI agent tools) AND the old app
 * (`~/development/supplymover/src/service/client-visit.service.ts`).
 *
 * Distribution refactor history: Rafi previously added 9 routing/delivery
 * columns to this table (`m_sm_fk_route_stop__id`,
 * `m_sm_fk_execution__id`, `m_sm_sequence`, `m_sm_visit_status`,
 * `m_sm_delivery_failure_reason`, `m_sm_proof_signature`,
 * `m_sm_proof_photo`, `m_sm_actual_arrival`, `m_sm_actual_departure`)
 * and the routes module created visit rows on delivery-stop completion
 * — conflating sales visits with delivery stops. All those columns are
 * dropped; delivery completion data lives natively on `trip_stop` now.
 */
@Entity('m_sm_client_visit')
@Index('ix_client_user', ['clientId', 'workdUserId'])
// PD-16801 — the Visit Log report and the KPI visit counts filter every
// visit in a date window regardless of client.
@Index('ix_visit_date', ['date'])
// PD-16477 — an import looks its earlier visits up by external id on every run.
@Index('ix_visit_external_id', ['externalId'])
export class ClientVisitEntity extends BaseEntity {
	@Column({ name: 'm_sm_fk_client__id', type: 'int', unsigned: true })
	public clientId!: number;

	@Column({ name: 'm_sm_workdUserId', type: 'int', unsigned: true })
	public workdUserId!: number;

	@Column({ name: 'm_sm_date', type: 'datetime', nullable: true })
	public date?: Date;

	@Column({
		name: 'm_sm_latitude',
		type: 'decimal',
		precision: 10,
		scale: 8,
		nullable: true,
	})
	public latitude?: number | null;

	@Column({
		name: 'm_sm_longitude',
		type: 'decimal',
		precision: 11,
		scale: 8,
		nullable: true,
	})
	public longitude?: number | null;

	/**
	 * PD-15918 (decision 22) — how far the rep's device was from the client's
	 * address when the visit was logged, in meters (Haversine), and whether
	 * that distance falls inside the delivery GPS threshold
	 * (`isGpsWithinThreshold`). Computed on EVERY visit create regardless of
	 * how it was logged; null distance = either side had no coordinates.
	 */
	@Column({ name: 'gpsDistanceMeters', type: 'int', nullable: true })
	public gpsDistanceMeters?: number | null;

	@Column({ name: 'gpsValidated', type: 'boolean', default: false })
	public gpsValidated!: boolean;

	/**
	 * The sales trip stop that produced this visit (decision 2). Nullable —
	 * ad-hoc visits have no stop. Kept as a plain column (no relation) so the
	 * visit entity does not import the trip graph; `SalesTripStopEntity`
	 * carries `clientVisitId` back the other way.
	 */
	@Column({
		name: 'fk_sales_trip_stop__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public salesTripStopId?: number | null;

	/**
	 * PD-16801 — what the rep recorded about the visit. All nullable (or
	 * defaulted) so the legacy CRM, which inserts this table by named columns,
	 * keeps working without knowing about them.
	 *
	 * `status` defaults to completed, so existing visits and SM1's inserts read
	 * as visits that happened. Only completed visits count as visits (see
	 * `ClientVisitStatus`).
	 */
	@Column({
		name: 'status',
		type: 'varchar',
		length: 20,
		default: ClientVisitStatus.Completed,
	})
	public status!: ClientVisitStatus;

	/**
	 * How the visit was made (On-site, Phone, ...). The NAME of a
	 * `client_visit_type` catalog value, stored verbatim like call
	 * dispositions, so a renamed or deleted value keeps rendering on the
	 * visits that used it.
	 */
	@Column({ name: 'visitType', type: 'varchar', length: 255, nullable: true })
	public visitType?: string | null;

	@Column({ name: 'subject', type: 'varchar', length: 255, nullable: true })
	public subject?: string | null;

	@Column({ name: 'objective', type: 'text', nullable: true })
	public objective?: string | null;

	@Column({ name: 'keyTakeaways', type: 'text', nullable: true })
	public keyTakeaways?: string | null;

	@Column({ name: 'nextSteps', type: 'text', nullable: true })
	public nextSteps?: string | null;

	/** Who the rep met. A plain column: the visit does not load the contact. */
	@Column({
		name: 'fk_client_contact__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientContactId?: number | null;

	/**
	 * The result of the visit, the NAME of a `client_visit_outcome` catalog
	 * value (stored verbatim, as with `visitType`).
	 */
	@Column({ name: 'outcome', type: 'varchar', length: 255, nullable: true })
	public outcome?: string | null;

	/**
	 * PD-16477 — the id of the record in the outside system a visit was
	 * imported from (for example a meeting in a CRM a tenant is moving off).
	 * An import can be run again and again, and this is what lets it find the
	 * visit it already created instead of adding a second one. Null on every
	 * visit logged in Workd.
	 *
	 * SM1 exposure: none. The legacy CRM inserts visit rows without naming the
	 * column and never reads it, and the column is nullable with no default.
	 */
	@Column({ name: 'externalId', type: 'varchar', length: 255, nullable: true })
	public externalId?: string | null;

	@ManyToOne(() => ClientEntity, (client) => client.clientVisits)
	@JoinColumn({ name: 'm_sm_fk_client__id' })
	public fkClient!: ClientEntity;

	/**
	 * PD-14327 — the client notes logged with this visit. The Log Visit flow
	 * writes at most one, but this is a one-to-many because `client_note` is
	 * the owning side and nothing stops a second note being linked later.
	 */
	@OneToMany(() => ClientNoteEntity, (clientNote) => clientNote.fkClientVisit)
	public clientNotes!: Array<ClientNoteEntity>;
}
